import { ColumnDef, Row } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';

import {
    BillingGroupHistoryAccountFee,
    BillingGroupHistoryPeriod,
    BillingGroupRun,
    DateStr,
    PaymentStatus,
} from '@ndvr/billing-api';

import { ClientDataTable } from '@/components/table/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { formatCurrency, formatCurrencyShort, formatLocaleDate, parseLocalDate } from 'utils';
import { useAccountLookup } from 'views/portfolio/account-queries';

import { resolveAccountName } from '../billing-groups/utils';
import { useBillingGroupHistory } from './billing-group-history-queries';
import { parseAmount, PAYMENT_STATUS_CLASSNAME, PAYMENT_STATUS_LABEL } from './billing-run-table';
import { billingDateLabel } from './utils';

interface Props {
    run: BillingGroupRun;
    onClose: () => void;
}

// Convert native date input values to the service's trailing-Z LocalDate form.
const toDateStr = (nativeValue: string): DateStr | undefined =>
    nativeValue ? formatLocaleDate(parseLocalDate(nativeValue)) : undefined;

const formatOutstanding = (amount: number) => (amount === 0 ? '—' : formatCurrency(amount));

const NUMBER_CELL_META = { cellClassName: 'w-[1%] text-right whitespace-nowrap' };

const BillingGroupSummary = ({
    period,
    accountNameByGUID,
}: {
    period: BillingGroupHistoryPeriod;
    accountNameByGUID: Map<string, string>;
}) => {
    const nameFor = useCallback(
        (fee: BillingGroupHistoryAccountFee) => accountNameByGUID.get(fee.accountGUID) ?? fee.accountGUID,
        [accountNameByGUID],
    );
    const sortedFees = useMemo(
        () =>
            [...period.accountFees].sort((a, b) =>
                nameFor(a).localeCompare(nameFor(b), undefined, { sensitivity: 'base' }),
            ),
        [period, nameFor],
    );

    const columns = useMemo<ColumnDef<BillingGroupHistoryAccountFee>[]>(
        () => [
            {
                id: 'account',
                accessorFn: (fee) => nameFor(fee),
                header: 'Account',
                footer: () => 'Total',
                meta: { headerClassName: 'bg-gray-50' },
            },
            {
                id: 'billableAum',
                accessorFn: (fee) => parseAmount(fee.billableAum),
                header: 'Billable Value',
                cell: ({ getValue }) => formatCurrencyShort(getValue<number>()),
                footer: () => formatCurrencyShort(parseAmount(period.billableAum)),
                meta: { ...NUMBER_CELL_META, headerClassName: `${NUMBER_CELL_META.cellClassName} bg-gray-50` },
            },
            {
                id: 'chargedAmount',
                accessorFn: (fee) => parseAmount(fee.chargedAmount),
                header: 'Billed Amount',
                cell: ({ getValue }) => formatCurrency(getValue<number>()),
                footer: () => formatCurrency(parseAmount(period.chargedAmount)),
                meta: { ...NUMBER_CELL_META, headerClassName: `${NUMBER_CELL_META.cellClassName} bg-gray-50` },
            },
            {
                id: 'outstandingAmount',
                accessorFn: (fee) => parseAmount(fee.outstandingAmount),
                header: 'Outstanding',
                cell: ({ getValue }) => formatOutstanding(getValue<number>()),
                footer: () => formatOutstanding(parseAmount(period.outstandingAmount)),
                meta: { ...NUMBER_CELL_META, headerClassName: `${NUMBER_CELL_META.cellClassName} bg-gray-50` },
            },
        ],
        [period, nameFor],
    );

    return (
        <div className="px-8 py-4">
            <div className="mb-2 text-sm font-semibold text-gray-900">Billing group summary</div>
            <ClientDataTable
                className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                data={sortedFees}
                columns={columns}
            />
        </div>
    );
};

export const BillingGroupHistoryDrawer = ({ run, onClose }: Props) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const { data: history, isLoading } = useBillingGroupHistory(
        run.billingGroupGUID,
        toDateStr(startDate),
        toDateStr(endDate),
    );

    const accountGUIDs = useMemo(
        () => [
            ...new Set((history?.periods ?? []).flatMap((period) => period.accountFees.map((fee) => fee.accountGUID))),
        ],
        [history],
    );
    const { data: accountLookups = [] } = useAccountLookup(accountGUIDs);
    const accountNameByGUID = useMemo(
        () => new Map(accountLookups.map((lookup) => [lookup.accountGUID, resolveAccountName(lookup)])),
        [accountLookups],
    );

    const totalCharged = parseAmount(history?.totalChargedAmount ?? '0');
    const totalSettled = parseAmount(history?.totalSettledAmount ?? '0');
    const totalOutstanding = parseAmount(history?.totalOutstandingAmount ?? '0');
    const paymentStatus = history?.paymentStatus ?? PaymentStatus.PAID;

    const columns = useMemo<ColumnDef<BillingGroupHistoryPeriod>[]>(
        () => [
            {
                id: 'billingDate',
                accessorFn: (period) => period.billingDate,
                header: 'Billing Period',
                cell: ({ getValue, row }) => (
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            aria-label={row.getIsExpanded() ? 'Collapse' : 'Expand'}
                            onClick={row.getToggleExpandedHandler()}
                        >
                            {row.getIsExpanded() ? (
                                <LuChevronDown className="h-4 w-4" />
                            ) : (
                                <LuChevronRight className="h-4 w-4" />
                            )}
                        </Button>
                        {billingDateLabel(getValue<DateStr>())}
                    </div>
                ),
                footer: () => 'Total',
                meta: {
                    headerClassName: 'bg-gray-50',
                },
            },
            {
                // AUM is a point-in-time figure and cannot be summed across periods.
                id: 'billableAum',
                accessorFn: (period) => parseAmount(period.billableAum),
                header: 'Billable AUM',
                cell: ({ getValue }) => formatCurrencyShort(getValue<number>()),
                meta: {
                    cellClassName: 'w-[1%] text-right whitespace-nowrap',
                    headerClassName: 'w-[1%] text-right whitespace-nowrap bg-gray-50',
                },
            },
            {
                id: 'chargedAmount',
                accessorFn: (period) => parseAmount(period.chargedAmount),
                header: 'Billed Amount',
                cell: ({ getValue }) => formatCurrency(getValue<number>()),
                footer: () => formatCurrency(totalCharged),
                meta: {
                    cellClassName: 'w-[1%] text-right whitespace-nowrap',
                    headerClassName: 'w-[1%] text-right whitespace-nowrap bg-gray-50',
                },
            },
            {
                id: 'settledAmount',
                accessorFn: (period) => parseAmount(period.settledAmount),
                header: 'Settled',
                cell: ({ getValue }) => formatCurrency(getValue<number>()),
                footer: () => formatCurrency(totalSettled),
                meta: {
                    cellClassName: 'w-[1%] text-right whitespace-nowrap',
                    headerClassName: 'w-[1%] text-right whitespace-nowrap bg-gray-50',
                },
            },
            {
                id: 'outstandingAmount',
                accessorFn: (period) => parseAmount(period.outstandingAmount),
                header: 'Outstanding',
                cell: ({ getValue }) => formatOutstanding(getValue<number>()),
                footer: () => formatOutstanding(totalOutstanding),
                meta: {
                    cellClassName: 'w-[1%] text-right whitespace-nowrap',
                    headerClassName: 'w-[1%] text-right whitespace-nowrap bg-gray-50',
                },
            },
            {
                id: 'paymentStatus',
                accessorFn: (period) => period.paymentStatus,
                header: 'Status',
                cell: ({ getValue }) => {
                    const status = getValue<PaymentStatus>();
                    return (
                        <Badge variant="outline" className={PAYMENT_STATUS_CLASSNAME[status]}>
                            {PAYMENT_STATUS_LABEL[status]}
                        </Badge>
                    );
                },
                footer: () => (
                    <Badge variant="outline" className={PAYMENT_STATUS_CLASSNAME[paymentStatus]}>
                        {PAYMENT_STATUS_LABEL[paymentStatus]}
                    </Badge>
                ),
                meta: {
                    cellClassName: 'w-[1%] whitespace-nowrap',
                    headerClassName: 'w-[1%] whitespace-nowrap bg-gray-50',
                },
            },
        ],
        [totalCharged, totalSettled, totalOutstanding, paymentStatus],
    );

    return (
        <Sheet defaultOpen onExitComplete={onClose}>
            <SheetContent aria-describedby={undefined} layout="scroll-body" size="2xl">
                <SheetHeader variant="pinned">
                    <SheetTitle>Billing History — {run.billingGroupName ?? run.billingGroupGUID}</SheetTitle>
                </SheetHeader>
                <div className="flex shrink-0 items-center gap-4 border-b border-gray-100 px-8 py-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        From
                        <input
                            type="date"
                            className="rounded border border-gray-300 px-2 py-1 text-sm"
                            value={startDate}
                            max={endDate || undefined}
                            onChange={(event) => setStartDate(event.target.value)}
                        />
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        To
                        <input
                            type="date"
                            className="rounded border border-gray-300 px-2 py-1 text-sm"
                            value={endDate}
                            min={startDate || undefined}
                            onChange={(event) => setEndDate(event.target.value)}
                        />
                    </label>
                    {(startDate || endDate) && (
                        <button
                            type="button"
                            className="text-sm text-gray-500 underline"
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>
                <SheetBody>
                    <div className="px-8 py-6">
                        <ClientDataTable
                            className="overflow-hidden rounded-xl border border-gray-200"
                            data={history?.periods ?? []}
                            columns={columns}
                            isLoading={isLoading}
                            emptyMessage="No billing history for this range."
                            initialState={{ sorting: [{ id: 'billingDate', desc: true }] }}
                            getRowCanExpand={(row: Row<BillingGroupHistoryPeriod>) =>
                                row.original.accountFees.length > 0
                            }
                            renderRowDetails={(period) => (
                                <BillingGroupSummary period={period} accountNameByGUID={accountNameByGUID} />
                            )}
                        />
                    </div>
                </SheetBody>
            </SheetContent>
        </Sheet>
    );
};
